// PR-J — TPS Oversight staff console transport. Cross-TPS directory +
// certification status, the approval/correction monitoring queues, fee
// configuration, money pass-through, and the break-glass emergency switch.
// Reads are gated server-side on Portfolio.OversightView; the fee editor on
// Tps.ManageFeeSchedule; the emergency switch on Portfolio.EmergencySwitchTps.
// Percent amounts (fee Percent rows, the collections-recovery rate) are
// FRACTIONS on the wire — pages convert ×100/÷100 for display/entry.
// Hooks in @/hooks/portfolioAdmin/useTpsOversight wrap these with TanStack
// Query; the API client unwraps ApiResponse<T> to T directly.

import { api } from "@/api/client";

// ——— Dashboard ———

export interface TpsOversightSummaryDto {
  tpsCount: number;
  certifiedCount: number;
  pendingBillApprovals: number;
  pendingDistributions: number;
  returnedBills: number;
  attentionDistributions: number;
  clearedUndistributed: number;
}

export interface TpsOversightRowDto {
  tpsId: string;
  name: string;
  isDefault: boolean;
  isCertified: boolean;
  certifiedAt: string | null;
  status: string;
  stripeOnboardingComplete: boolean;
  clearingAccountStatus: string;
  commissionsAccountStatus: string;
  assignedLoanCount: number;
  pendingBillCount: number;
  pendingDistributionCount: number;
  returnedBillCount: number;
  attentionDistributionCount: number;
  clearedUndistributed: number;
  /** Current collections-recovery fee rate as a FRACTION (0.15 = 15%), or null. */
  collectionsRecoveryFeeRate: number | null;
  /** Section 3 — true when the Clearing account is on a MANUAL payout schedule
   * (platform-controlled; Stripe never auto-sweeps to the servicer's bank). */
  clearingPayoutControlled: boolean;
  /** The Clearing account's last-synced payout interval ("manual"/"daily"/…), or null. */
  clearingPayoutInterval: string | null;
}

export interface TpsOversightDashboardDto {
  summary: TpsOversightSummaryDto;
  tps: TpsOversightRowDto[];
}

// ——— Assigned loans (per TPS) ———

export interface TpsAssignedLoanDto {
  loanId: string;
  loanNumber: string;
  acceptanceStatus: string;
  nextTpsId: string | null;
  pendingBillCount: number;
  pendingDistributionCount: number;
}

// ——— Approval queue ———

export interface TpsPendingBillDto {
  statementId: string;
  loanId: string;
  loanNumber: string;
  tpsId: string | null;
  tpsName: string | null;
  periodStart: string;
  periodEnd: string;
  amountDue: number;
  preparedAt: string;
  ageDays: number;
}

export interface TpsPendingDistributionDto {
  distributionId: string;
  loanId: string;
  loanNumber: string;
  tpsId: string;
  tpsName: string | null;
  periodStart: string;
  totalAmount: number;
  blockReason: string | null;
  fundsClearedAt: string | null;
  createdAt: string;
  ageDays: number;
}

export interface TpsApprovalQueueDto {
  pendingBills: TpsPendingBillDto[];
  pendingDistributions: TpsPendingDistributionDto[];
}

// ——— Correction queue ———

/**
 * Returned bill row. PR-E's staff statement DTO is not exported under
 * @/api/portfolioAdmin, so this minimal local shape carries only the
 * fields the correction queue renders.
 */
export interface TpsReturnedBillDto {
  id: string;
  loanId: string;
  loanNumber: string;
  tpsId: string | null;
  tpsName: string | null;
  periodStart: string;
  periodEnd: string;
  statementStatus: string;
  amountDue: number;
  returnedReason: string | null;
  preparedAt: string;
}

export interface TpsAttentionDistributionDto {
  distributionId: string;
  loanId: string;
  loanNumber: string;
  tpsId: string;
  tpsName: string | null;
  status: string;
  blockReason: string | null;
  reversalReason: string | null;
  totalAmount: number;
  createdAt: string;
}

export interface TpsCorrectionQueueDto {
  returnedBills: TpsReturnedBillDto[];
  attentionDistributions: TpsAttentionDistributionDto[];
}

// ——— Money oversight ———

export interface TpsMoneyOversightDto {
  clearingInTotal: number;
  distributedOutTotal: number;
  pendingDistributionTotal: number;
  reversedTotal: number;
  transfersCreated: number;
  transfersReversed: number;
}

// ——— Clearing-account fund-safety anomalies (Section 3) ———

export interface TpsClearingAnomalyDto {
  id: string;
  tpsId: string;
  tpsName: string | null;
  loanId: string | null;
  /** "UnexpectedPayout" | "BankAccountChanged" | "BalanceShortfall". */
  kind: string;
  amount: number | null;
  expectedAmount: number | null;
  actualAmount: number | null;
  message: string;
  detectedAt: string;
  acknowledged: boolean;
  acknowledgedAt: string | null;
}

// ——— Fee schedule ———

export type TpsFeeType =
  | "BoardingFee"
  | "MonthlyServicingFee"
  | "PerPaymentFee"
  | "LateFee"
  | "NsfFee"
  | "PayoffFee"
  | "ModificationFee"
  | "DocRetrievalFee"
  | "YearEndFee"
  | "AchWireFee";

export type TpsFeeAmountType = "Flat" | "Percent";

export interface TpsFeeScheduleDto {
  id: string;
  tpsId: string;
  feeType: TpsFeeType;
  amountType: TpsFeeAmountType;
  /** Percent rows store a FRACTION (e.g. 0.0125 = 1.25%); Flat is dollars. */
  amount: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  notes: string | null;
  createdAt: string;
  createdByUserId: string | null;
  /** SP-3 — collection mode for MonthlyServicingFee rows, one of
   * "deduct_from_payment" | "charge_to_borrower" (null = deduct). */
  mode: string | null;
}

export interface CreateTpsFeeScheduleRequest {
  feeType: TpsFeeType;
  amountType: TpsFeeAmountType;
  amount: number;
  /** ISO instant; re-adding the open row's date updates it in place, else must
   * be strictly after the prior open row for the feeType. */
  effectiveFrom: string;
  notes: string | null;
  /** Collection mode captured for every fee type (null = deduct_from_payment). */
  mode?: string | null;
}

export interface UpdateTpsFeeScheduleBody {
  amountType: TpsFeeAmountType;
  amount: number;
  /** Collection mode (null = deduct_from_payment). */
  mode: string | null;
  notes: string | null;
}

export interface CollectionsRecoveryRateDto {
  /** FRACTION in [0,1) (0.15 = 15%) or null. */
  rate: number | null;
}

// ——— Emergency switch ———

export interface EmergencyTpsSwitchRequest {
  newTpsId: string;
  reason: string;
}

export interface EmergencyTpsSwitchResultDto {
  loanId: string;
  previousTpsId: string | null;
  newTpsId: string;
  voidedBills: number;
  voidedDistributions: number;
}

const BASE = "/portfolio-admin/tps-oversight";

export const tpsOversightApi = {
  // Reads
  dashboard: () => api.get<TpsOversightDashboardDto>(BASE),
  loansForTps: (tpsId: string) =>
    api.get<TpsAssignedLoanDto[]>(
      `${BASE}/tps/${encodeURIComponent(tpsId)}/loans`,
    ),
  approvalQueue: () => api.get<TpsApprovalQueueDto>(`${BASE}/approval-queue`),
  correctionQueue: () =>
    api.get<TpsCorrectionQueueDto>(`${BASE}/correction-queue`),
  money: () => api.get<TpsMoneyOversightDto>(`${BASE}/money`),
  anomalies: (acknowledged?: boolean) =>
    api.get<TpsClearingAnomalyDto[]>(
      `${BASE}/anomalies${
        acknowledged === undefined ? "" : `?acknowledged=${acknowledged}`
      }`,
    ),
  acknowledgeAnomaly: (id: string) =>
    api.post<boolean>(`${BASE}/anomalies/${encodeURIComponent(id)}/acknowledge`, {}),

  // Fee editor
  feeSchedule: (tpsId: string) =>
    api.get<TpsFeeScheduleDto[]>(
      `/portfolio-admin/tps/${encodeURIComponent(tpsId)}/fee-schedule`,
    ),
  createFeeSchedule: (tpsId: string, body: CreateTpsFeeScheduleRequest) =>
    api.post<TpsFeeScheduleDto>(
      `/portfolio-admin/tps/${encodeURIComponent(tpsId)}/fee-schedule`,
      body,
    ),
  updateFeeSchedule: (
    tpsId: string,
    feeId: string,
    body: UpdateTpsFeeScheduleBody,
  ) =>
    api.put<TpsFeeScheduleDto>(
      `/portfolio-admin/tps/${encodeURIComponent(tpsId)}/fee-schedule/${encodeURIComponent(feeId)}`,
      body,
    ),
  deleteFeeSchedule: (tpsId: string, feeId: string) =>
    api.delete<void>(
      `/portfolio-admin/tps/${encodeURIComponent(tpsId)}/fee-schedule/${encodeURIComponent(feeId)}`,
    ),
  setCollectionsRecoveryRate: (tpsId: string, rate: number | null) =>
    api.put<CollectionsRecoveryRateDto>(
      `/portfolio-admin/tps/${encodeURIComponent(tpsId)}/fee-schedule/collections-recovery-rate`,
      { rate },
    ),

  // Emergency switch
  emergencySwitch: (loanId: string, body: EmergencyTpsSwitchRequest) =>
    api.post<EmergencyTpsSwitchResultDto>(
      `/portfolio-admin/loans/${encodeURIComponent(loanId)}/tps-assignment/emergency-switch`,
      body,
    ),
};
